<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'fal fa-first-aid'"
      :section-title="$tc('configuration.sections.healthManagementArea', 2)"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteHealthManagementAreas(selectedRows)"
        @shortkey="deleteHealthManagementAreas(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showHealthManagementAreasModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showHealthManagementAreasModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showHealthManagementAreasModal($t('configuration.actions.add'))"
        @shortkey="showHealthManagementAreasModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="healthManagementAreasModalWrapper">
      <health-management-area-configuration-modal
        :action-title="actionTitle"
        @update="patchHealthManagementAreas($event)"
        @create="createHealthManagementAreas($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import ConfigurationHealthManagementAreasGridColumns from '@/modules/configurationModule/detail/centersAndServices/healthManagementAreas/ConfigurationHealthManagementAreasGridColumns'
import HealthManagementAreaConfigurationModal from '@/modules/configurationModule/detail/centersAndServices/healthManagementAreas/HealthManagementAreaConfigurationModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import ConfigurationHealthManagementAreaServices
    from '@/api/resources/configuration/centersAndAreas/healthManagementArea/ConfigurationHealthManagementAreaServices'

export default {
    name: 'HealthManagementAreasConfigurationGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        HealthManagementAreaConfigurationModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.centersAndServices.healthManagementAreas',
            actionTitle: null,
            selectedRows: [],
            columnDefs: ConfigurationHealthManagementAreasGridColumns(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveArea() {
            return !!this.selectedRows.find(healthManagementArea => healthManagementArea.active)
        },
        hasDeactiveArea() {
            return !!this.selectedRows.find(healthManagementArea => !healthManagementArea.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(healthManagementArea => healthManagementArea.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(healthManagementArea => !healthManagementArea.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveArea && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactiveArea && !this.hasAllActivated
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const healthManagementAreasToPatch = selectionBatch.filter(healthManagementArea => healthManagementArea.active !== option).map(healthManagementArea => {
                return {
                    active: option,
                    id: healthManagementArea.id
                }
            })
            if (healthManagementAreasToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(ConfigurationHealthManagementAreaServices.patchHealthManagementAreasBatch(healthManagementAreasToPatch))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobalFromErrorResponse(err)
                    return
                }
                if (selectionBatch.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 2, { elements: healthManagementAreasToPatch.length }))
                else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async patchHealthManagementAreas(...healthManagementAreas) {
            this.updateLoading(true)
            const [err, data] = await this.toCall(ConfigurationHealthManagementAreaServices.patchHealthManagementAreasBatch(healthManagementAreas))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (data.length) {
                this.handleErrors(data)
                return
            }
            this.$refs.healthManagementAreasModalWrapper.hide()
            this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
            this.reloadData()
        },
        async createHealthManagementAreas(healthManagementArea) {
            this.updateLoading(true)
            const [err] = await this.toCall(ConfigurationHealthManagementAreaServices.createHealthManagementArea(healthManagementArea))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            }
            this.$refs.healthManagementAreasModalWrapper.hide()
            this.showToastSuccessGlobal(this.$t('configuration.toastMessages.created'))
            this.reloadData()
        },
        async deleteHealthManagementAreasBatch(healthManagementAreas) {
            const healthManagementAreasIds = healthManagementAreas.map(area => area.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(ConfigurationHealthManagementAreaServices.deleteHealthManagementAreasBatch(healthManagementAreasIds))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (healthManagementAreasIds.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: healthManagementAreasIds.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        async deleteHealthManagementAreas(healthManagementAreas) {
            if (healthManagementAreas.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteHealthManagementAreasBatch(healthManagementAreas)
                }
            }
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(ConfigurationHealthManagementAreaServices))
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showHealthManagementAreasModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deleteHealthManagementAreas(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(ConfigurationHealthManagementAreaServices))
        },
        showHealthManagementAreasModal(title, selectedHealthManagementArea) {
            this.actionTitle = title
            this.$refs.healthManagementAreasModalWrapper.show([selectedHealthManagementArea])
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
